import { createContext, useEffect, useMemo, useContext, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { productionApi } from '~/api/production';
import { getPreviousWednesday } from '~/utils/dateUtils/dateUtils';

import { DEFAULT_FORM_DATA } from '../../utils/constants';

const DataLoaderContext = createContext({});

/**
 * Provider component that loads and manages server data for the Storefront Configuration Editor.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that will have access to the DataLoaderContext
 * @returns {JSX.Element} A context provider wrapping the children with configuration data
 */
export function DataLoaderProvider({ children }) {
    const { id } = useParams();
    const isCreateMode = id === 'new';

    const searchParams = new URLSearchParams(useLocation().search);
    const packGroupIDParam = searchParams.get('packGroupID');

    const [config, setConfig] = useState(null);
    const [packGroupDate, setPackGroupDate] = useState();

    useEffect(() => {
        const fetchConfig = async () => {
            if (isCreateMode) {
                setConfig(DEFAULT_FORM_DATA);
                return;
            }

            try {
                const response = await productionApi.getStorefrontConfigurationByID(id);
                setConfig(response.data.storefrontConfiguration);
            } catch (error) {
                console.error('Error fetching configuration:', error);
            }
        };
        fetchConfig();
    }, [id, isCreateMode]);

    useEffect(() => {
        const packGroupID = config?.packGroupID || packGroupIDParam;
        if (!packGroupID) return;

        const fetchPackGroupDates = async () => {
            try {
                const response = await productionApi.getPackGroupDates(packGroupID);
                const firstDateOfWeek = new Date(
                    response.data.dateAndDays.find((date) => date.day === 'sunday').date.date,
                );
                const previousWednesday = new Date(getPreviousWednesday(firstDateOfWeek));

                setPackGroupDate(previousWednesday);
            } catch (error) {
                console.error('Error fetching pack group dates:', error);
            }
        };

        fetchPackGroupDates();
    }, [config?.packGroupID, packGroupIDParam]);

    const value = useMemo(
        () => ({
            config,
            isCreateMode,
            packGroupDate,
            setConfig,
        }),
        [config, isCreateMode, packGroupDate],
    );

    return <DataLoaderContext.Provider value={value}>{children}</DataLoaderContext.Provider>;
}

export function useDataLoader() {
    return useContext(DataLoaderContext);
}
